import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import './Patterns.css';

export interface SortState {
  key: string;
  direction: 'asc' | 'desc';
}

export interface DataTableColumn<T> {
  key: string;
  label: ReactNode;
  /** Custom cell renderer. Defaults to (row) => (row as any)[key]. */
  render?: (row: T) => ReactNode;
  /** Cell text alignment. Default 'left'. */
  align?: 'left' | 'right' | 'center';
  /** When true, the header becomes a clickable button with aria-sort. */
  sortable?: boolean;
}

export interface PaginationProps {
  page: number;        // 1-indexed
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  pagination?: PaginationProps;
  /** Rendered when rows.length === 0. */
  emptyState?: ReactNode;
  /** Renders a row as a phone-friendly card. When provided, the cards container is added to the DOM and CSS hides it at >= 768px. */
  mobileCardRow?: (row: T) => ReactNode;
  /** When provided AND expandedRowKey matches a row, renders this content as a full-width tr below the row. */
  expandedRowRender?: (row: T) => ReactNode;
  /**
   * Which row(s) are currently expanded. Pass a string for single-expansion,
   * a Set of rowKey values for multi-expansion, or null/undefined for none.
   */
  expandedRowKey?: string | Set<string> | null;
  /** Optional per-row class (e.g. "text-muted" for soft-deleted rows). */
  rowClassName?: (row: T) => string;
  /**
   * Optional per-row extra props applied directly to the `<tr>` element.
   * Useful for drag-and-drop, row-level event handlers, inline styles, or
   * additional ARIA attributes. Returned object is spread onto the `<tr>`.
   */
  rowProps?: (row: T, index: number) => React.HTMLAttributes<HTMLTableRowElement> & { draggable?: boolean };
  /**
   * Current sort state. When set, the matching column's header gets aria-sort
   * and the sortable indicator reflects the direction. Controlled — the parent
   * owns the sort state and updates it via onSortChange.
   */
  sort?: SortState | null;
  /**
   * Called when the user clicks a sortable header. Receives the next sort
   * state: toggles direction if the same column is already sorted, else
   * defaults to 'asc' on the newly-clicked column.
   */
  onSortChange?: (sort: SortState) => void;
  /**
   * Totals/summary rendered under the table, INDEX-ALIGNED to `columns` — cell
   * i sits beneath column i and inherits its alignment. Pass null/'' for any
   * column with nothing to total.
   *
   * Callers used to render totals as a flex <div> after the table, which left
   * a row of bare numbers with no relationship to the columns above them.
   *
   * Below the card breakpoint there are no columns to align to, so the cards
   * get a labelled list instead, built from each column's own `label`. That is
   * derived here rather than asked for twice — a second prop is a second thing
   * to forget, which is how the card view has lost controls three times.
   */
  footerRow?: Array<ReactNode>;
  /** Accessible caption for the footer region. Defaults to "Totals". */
  footerLabel?: string;
}

function defaultRender<T>(row: T, key: string): ReactNode {
  const v = (row as unknown as Record<string, unknown>)[key];
  return typeof v === 'string' || typeof v === 'number' ? String(v) : null;
}

function Pagination({ page, pageSize, total, onPageChange }: PaginationProps) {
  const { t } = useTranslation();
  const start = total === 0 ? 0 : Math.min((page - 1) * pageSize + 1, total);
  const end = Math.min(page * pageSize, total);
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="pat-data-table__pagination">
      <span className="pat-data-table__pagination-summary">
        {t('patterns.dataTable.paginationSummary', { start, end, total })}
      </span>
      <div className="pat-data-table__pagination-pager">
        <button
          type="button"
          className="pat-data-table__pagination-btn"
          onClick={() => onPageChange(Math.max(1, page - 1))}
          disabled={page <= 1}
          aria-label={t('patterns.dataTable.previousPage')}
        >‹</button>
        <span className="pat-data-table__pagination-current">{page} / {lastPage}</span>
        <button
          type="button"
          className="pat-data-table__pagination-btn"
          onClick={() => onPageChange(Math.min(lastPage, page + 1))}
          disabled={page >= lastPage}
          aria-label={t('patterns.dataTable.nextPage')}
        >›</button>
      </div>
    </div>
  );
}

/** A footer cell counts as present only if it would render something. */
function hasFooterContent(cell: ReactNode): boolean {
  return cell !== null && cell !== undefined && cell !== false && cell !== '';
}

function DataTable<T>({ columns, rows, rowKey, onRowClick, pagination, emptyState, mobileCardRow, expandedRowRender, expandedRowKey, rowClassName, rowProps, sort, onSortChange, footerRow, footerLabel = 'Totals' }: DataTableProps<T>) {
  if (rows.length === 0 && emptyState !== undefined) {
    return <div className="pat-data-table pat-data-table--empty">{emptyState}</div>;
  }
  const extraClasses: string[] = rowClassName
    ? rows.map((row) => rowClassName(row))
    : [];
  return (
    <div className="pat-data-table">
      {/*
       * The table lives in its own scroll container. `.pat-data-table` sets
       * `overflow: hidden` to clip the border radius, which also clipped any
       * column past the viewport edge with no way to reach it — a wide table
       * on a phone in LANDSCAPE (~850px, above the 767px card breakpoint) lost
       * its trailing columns entirely. Keep the radius clipping on the outer
       * element and the horizontal scroll on this wrapper; do not merge them.
       */}
      <div className="pat-data-table__scroll">
      <table className="pat-data-table__table">
        <thead className="pat-data-table__thead">
          <tr>
            {columns.map((col) => {
              const isSorted = sort?.key === col.key;
              const ariaSort = isSorted
                ? (sort!.direction === 'asc' ? 'ascending' : 'descending')
                : undefined;
              const handleSortClick = () => {
                if (!col.sortable || !onSortChange) return;
                if (isSorted) {
                  onSortChange({
                    key: col.key,
                    direction: sort!.direction === 'asc' ? 'desc' : 'asc',
                  });
                } else {
                  onSortChange({ key: col.key, direction: 'asc' });
                }
              };
              return (
                <th
                  key={col.key}
                  scope="col"
                  aria-sort={ariaSort}
                  className={`pat-data-table__th${col.align ? ` pat-data-table__th--${col.align}` : ''}${col.sortable ? ' pat-data-table__th--sortable' : ''}`}
                >
                  {col.sortable ? (
                    <button
                      type="button"
                      className="pat-data-table__th-button"
                      onClick={handleSortClick}
                    >
                      <span className="pat-data-table__th-label">{col.label}</span>
                      <span
                        className="pat-data-table__sort-indicator"
                        aria-hidden="true"
                      >
                        {isSorted ? (sort!.direction === 'asc' ? '▲' : '▼') : '▲▼'}
                      </span>
                    </button>
                  ) : (
                    col.label
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => {
            const key = rowKey(row);
            const isExpanded = expandedRowRender !== undefined && (
              typeof expandedRowKey === 'string'
                ? expandedRowKey === key
                : expandedRowKey instanceof Set
                  ? expandedRowKey.has(key)
                  : false
            );
            const extraClass = extraClasses[idx] ?? '';
            const extraRowProps = rowProps ? rowProps(row, idx) : undefined;
            const extraRowClass = extraRowProps?.className ? ` ${extraRowProps.className}` : '';
            const extraRowOnClick = extraRowProps?.onClick;
            const baseRowClick = onRowClick ? () => onRowClick(row) : undefined;
            const mergedOnClick = extraRowOnClick && baseRowClick
              ? (e: React.MouseEvent<HTMLTableRowElement>) => {
                  extraRowOnClick(e);
                  if (!e.defaultPrevented) baseRowClick();
                }
              : (extraRowOnClick ?? baseRowClick);
            // Avoid passing className/onClick twice via the spread.
            const { className: _ignoredClass, onClick: _ignoredClick, ...restRowProps } = extraRowProps ?? {};
            return (
              <React.Fragment key={key}>
                <tr
                  {...restRowProps}
                  className={`pat-data-table__row${onRowClick ? ' pat-data-table__row--clickable' : ''}${extraClass ? ` ${extraClass}` : ''}${extraRowClass}`}
                  onClick={mergedOnClick}
                  data-rowkey={key}
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={`pat-data-table__td${col.align ? ` pat-data-table__td--${col.align}` : ''}`}
                    >
                      {col.render ? col.render(row) : defaultRender(row, col.key)}
                    </td>
                  ))}
                </tr>
                {isExpanded && (
                  <tr className="pat-data-table__expansion">
                    <td colSpan={columns.length}>{expandedRowRender(row)}</td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
        {footerRow && (
          <tfoot className="pat-data-table__tfoot" data-testid="data-table-footer">
            <tr className="pat-data-table__footer-row">
              {columns.map((col, i) => (
                <td
                  key={col.key}
                  data-col={col.key}
                  className={`pat-data-table__td pat-data-table__footer-cell${col.align ? ` pat-data-table__td--${col.align}` : ''}`}
                >
                  {footerRow[i] ?? null}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
      </div>
      {mobileCardRow && (
        <div className="pat-data-table__cards" data-testid="data-table-cards" role="list">
          {rows.map((row, idx) => {
            const extraClass = extraClasses[idx] ?? '';
            return (
              <div
                key={rowKey(row)}
                className={`pat-data-table__card${onRowClick ? ' pat-data-table__card--clickable' : ''}${extraClass ? ` ${extraClass}` : ''}`}
                role="listitem"
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {mobileCardRow(row)}
              </div>
            );
          })}
          {/*
           * The <tfoot> above lives inside the table, which CSS hides below
           * 768px — so on a phone the totals would vanish with it, exactly how
           * the expand control was lost before. Card view has no columns to
           * align to, so each figure is paired with its own column's label.
           */}
          {footerRow && (
            <div className="pat-data-table__cards-footer" data-testid="data-table-footer-cards">
              <span className="pat-data-table__cards-footer-label">{footerLabel}</span>
              {columns.map((col, i) => (
                hasFooterContent(footerRow[i]) ? (
                  <span key={col.key} data-col={col.key} className="pat-data-table__cards-footer-item">
                    {col.label ? <span className="pat-data-table__cards-footer-key">{col.label}</span> : null}
                    <span>{footerRow[i]}</span>
                  </span>
                ) : null
              ))}
            </div>
          )}
        </div>
      )}
      {pagination && <Pagination {...pagination} />}
    </div>
  );
}

export default DataTable;
